import React, { ReactNode } from 'react';
import { TypographyToken, useFullTheme } from '~/design';
import { useResponsive } from '~/utils';
import { TextProps } from '../Text';

interface DescriptionListContextProps {
  variant: DescriptionListProps['variant'];
}

const DescriptionListContext = React.createContext<DescriptionListContextProps | null>(null);

export interface DescriptionListProps {
  /** Use this prop to provide an accessible name to the list */
  'aria-label'?: string;

  /** Use this prop to link an element with an accessible name located outside to the list */
  'aria-labelledby'?: string;

  /** The content of the description list */
  children: React.ReactNode;

  /** `variant` will end up affecting the layout of the DescriptionList. It is now recommended to
   * use variant=free which allows you to create any layout you want within the DescriptionList.
   * (Examples can be seen on Storybook.) If you don't need this flexibility, use variant=regular.
   **/
  variant?: 'grid' | 'regular' | 'free';
}

/**
 * Description lists are a way to organize and explain related information.
 * They’re particularly useful when you need to list and define terms such as in a glossary or to display metadata (a list of key-value pairs).
 *
 * ## Accessibility ♿
 * - The description list component produces a description list wrapper (`<dl>`), terms (`<dt>`), and definitions (`<dd>`)
 *    to convey the relationships between the list items to assistive technology users.
 */
export function DescriptionList({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  children,
  variant = 'regular',
}: DescriptionListProps) {
  if (variant === 'grid') {
    console.warn('[DescriptionList] `variant=grid` is deprecated.');
  }

  const theme = useFullTheme();

  const variantStyle = (
    {
      grid: {
        columnGap: theme.spacing[4],
        display: 'inline-grid',
        gridTemplateColumns: `auto 1fr`,
        rowGap: theme.spacing[4],
      },
      regular: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing[4],
      },
      free: {},
    } as const
  )[variant];

  return (
    <DescriptionListContext.Provider value={{ variant }}>
      <dl
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        css={{ margin: 0, ...variantStyle }}
      >
        {children}
      </dl>
    </DescriptionListContext.Provider>
  );
}

export interface DescriptionItemProps {
  /** Sets how the term aligns with the details (vertically) */
  align?: 'start' | 'center' | 'end' | 'normal';

  /** The definition details */
  details: React.ReactNode;

  /** Sets the spacing between the term and the details */
  justify?: 'start' | 'space-between';

  /** The definition term */
  term: React.ReactNode;
}

/**
 * @deprecated Use `DescriptionList.Details` and `DescriptionList.Term` instead.
 */
DescriptionList.Item = function DescriptionListItem({
  align = 'normal',
  details,
  justify = 'space-between',
  term,
}: DescriptionItemProps) {
  const context = React.useContext(DescriptionListContext);
  if (!context) {
    throw new Error('`DescriptionList.Item` can only be rendered inside `DescriptionList`');
  }
  if (context.variant === 'free') {
    throw new Error(
      '[DescriptionList.Item] This component cannot be used in conjunction with `variant=free` on parent DescriptionList.',
    );
  }

  const { colors, spacing, typography } = useFullTheme();
  const responsive = useResponsive();
  const justifyStyle = (
    {
      'space-between': {
        textAlign: 'right',
      },
      start: {},
    } as const
  )[justify];

  const variantStyle = {
    grid: {
      display: 'contents',
    },
    regular: {
      alignItems: align,
      display: 'flex',
      gap: spacing[4],
      justifyContent: 'space-between',
    },
    free: {},
  }[context.variant ?? 'regular'];

  return (
    <div css={variantStyle}>
      <dt
        css={{
          ...responsive.css(typography.labelM),
          alignSelf: align,
          color: colors.textSecondaryStark,
        }}
      >
        {term}
      </dt>
      <dd
        css={[
          {
            ...responsive.css(typography.bodyM),
            color: colors.textSecondaryStark,
            margin: 0,
          },
          justifyStyle,
        ]}
      >
        {details}
      </dd>
    </div>
  );
};

interface DescriptionListElementProps {
  /** The alignment of the text. */
  align?: TextProps['align'];

  /** The content to render. */
  children: ReactNode;

  /** The intent of the text. */
  intent?: TextProps['intent'];

  /** The variant of the text. */
  variant?: TypographyToken;
}

DescriptionList.Details = function DescriptionListDetails({
  align,
  children,
  intent = 'secondary',
  variant = 'bodyM',
}: DescriptionListElementProps) {
  const context = React.useContext(DescriptionListContext);
  if (!context) {
    throw new Error('[DescriptionList.Details] Must be a child of `DescriptionList`.');
  }
  if (context.variant !== 'free') {
    throw new Error(
      '[DescriptionList.Details] This component must be used in conjunction with `variant=free` on parent DescriptionList.',
    );
  }
  const { colors, typography } = useFullTheme();
  const responsive = useResponsive();
  return (
    <dd
      css={[
        {
          ...responsive.css(typography[variant ?? 'bodyM']),
          color: {
            magic: colors.textMagicStark,
            negative: colors.textNegativeStark,
            positive: colors.textPositiveStark,
            primary: colors.textPrimaryStark,
            secondary: colors.textSecondaryStark,
            warning: colors.textWarningStark,
          }[intent],
          margin: 0,
        },
        responsive.css({ textAlign: align }),
      ]}
    >
      {children}
    </dd>
  );
};

DescriptionList.Term = function DescriptionListTerm({
  align,
  children,
  intent = 'secondary',
  variant = 'labelM',
}: DescriptionListElementProps) {
  const context = React.useContext(DescriptionListContext);
  if (!context) {
    throw new Error('[DescriptionList.Term] Must be a child of `DescriptionList`.');
  }
  if (context.variant !== 'free') {
    throw new Error(
      '[DescriptionList.Term] This component must be used in conjunction with `variant=free` on parent DescriptionList.',
    );
  }
  const { colors, typography } = useFullTheme();
  const responsive = useResponsive();
  return (
    <dt
      css={[
        {
          ...responsive.css(typography[variant ?? 'labelM']),
          color: {
            magic: colors.textMagicStark,
            negative: colors.textNegativeStark,
            positive: colors.textPositiveStark,
            primary: colors.textPrimaryStark,
            secondary: colors.textSecondaryStark,
            warning: colors.textWarningStark,
          }[intent],
        },
        responsive.css({ textAlign: align }),
      ]}
    >
      {children}
    </dt>
  );
};
